import React, { useEffect, useState } from 'react';
import { Save, FileText, ExternalLink, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import { STATIC_DEFAULTS } from '../../data/static_defaults';

const PAGES = [
  { slug: 'kvkk', label: 'KVKK Aydınlatma', path: '/kurumsal/kvkk', defaultTitle: 'Kişisel Verilerin Korunması' },
  { slug: 'tax', label: 'Vergi Muafiyeti', path: '/kurumsal/vergi', defaultTitle: 'Vergi Muafiyeti' },
  { slug: 'school', label: 'Nilüfer Gökay Okulu', path: '/kurumsal/okul', defaultTitle: 'Nilüfer Gökay Okulu' },
  { slug: 'units', label: 'Eğitim Destek Birimleri', path: '/kurumsal/birimler', defaultTitle: 'Eğitim Destek Birimleri' },
  { slug: 'boards', label: 'Kurullarımız', path: '/kurumsal/kurullar', defaultTitle: 'Kurullarımız' },
  { slug: 'about_history', label: 'Hakkımızda — Tarihçe', path: '/kurumsal#tarihce', defaultTitle: 'Tarihçemiz' },
  { slug: 'about_vision', label: 'Hakkımızda — Vizyon & Misyon', path: '/kurumsal#vizyon', defaultTitle: 'Vizyon & Misyon' },
];

export default function AdminStaticPages() {
  const [active, setActive] = useState(PAGES[0].slug);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [usingDefault, setUsingDefault] = useState(false);

  useEffect(() => {
    const cur = PAGES.find(p => p.slug === active);
    api.adminListSettings().then(list => {
      const map = {};
      list.forEach(s => { map[s.key] = s.value; });
      const saved = map[`static_${active}_content`];
      if (saved && saved.trim()) {
        setContent(saved);
        setUsingDefault(false);
      } else {
        // No override yet — prefill with the live-site default so admin can see & edit it
        setContent(STATIC_DEFAULTS[active] || '');
        setUsingDefault(true);
      }
      setTitle(map[`static_${active}_title`] || cur.defaultTitle);
    }).catch(() => {
      setContent(STATIC_DEFAULTS[active] || '');
      setUsingDefault(true);
      setTitle(cur.defaultTitle);
    });
  }, [active]);

  const save = async () => {
    setSaving(true);
    try {
      await api.adminUpsertSetting(`static_${active}_title`, title);
      await api.adminUpsertSetting(`static_${active}_content`, content);
      toast.success('Sayfa içeriği kaydedildi');
      setUsingDefault(false);
    } catch {
      toast.error('Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const resetToDefault = () => {
    if (!STATIC_DEFAULTS[active]) return;
    setContent(STATIC_DEFAULTS[active]);
    setUsingDefault(true);
    toast.info('Varsayılan içerik yüklendi. Kaydet\'e basmadıkça değişiklik uygulanmaz.');
  };

  const cur = PAGES.find(p => p.slug === active);

  return (
    <div data-testid="admin-static-pages">
      <div className="mb-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">İçerik</div>
        <h1 className="font-display text-4xl mt-2 leading-none">Statik Sayfalar</h1>
        <p className="mt-2 text-ink-400 text-sm">KVKK, Vergi Muafiyeti, Okul, Birimler gibi düz metin sayfalarını burada düzenleyin.</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <div className="bg-white rounded-2xl border border-ink-100 p-3 self-start">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-ink-400 font-bold">Sayfalar</div>
          <div className="space-y-0.5">
            {PAGES.map(p => (
              <button key={p.slug} onClick={() => setActive(p.slug)} data-testid={`static-${p.slug}`}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${active === p.slug ? 'bg-ink-600 text-cream' : 'text-ink-500 hover:bg-cream-200'}`}>
                <div className="font-semibold flex items-center gap-2"><FileText className="w-3.5 h-3.5 opacity-60" /> {p.label}</div>
                <div className={`text-[10px] mt-0.5 font-mono ${active === p.slug ? 'text-cream/60' : 'text-ink-400'}`}>{p.path}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-ink-100 p-6 lg:p-7">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold flex items-center gap-2">
                {cur.label}
                {usingDefault && (
                  <span data-testid="using-default-badge" className="inline-flex items-center gap-1 bg-gold/15 border border-gold/40 text-ink-600 rounded-full px-2 py-0.5 text-[9px] font-bold normal-case tracking-normal">
                    <Sparkles className="w-3 h-3" /> Canlı içerik önizlemesi
                  </span>
                )}
              </div>
              <a href={cur.path} target="_blank" rel="noopener noreferrer" className="text-xs text-ink-400 hover:text-ink-600 mt-1 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Sayfayı görüntüle</a>
            </div>
            <div className="flex items-center gap-2">
              {STATIC_DEFAULTS[active] && !usingDefault && (
                <button
                  onClick={resetToDefault}
                  data-testid="reset-default"
                  className="border border-ink-200 text-ink-500 rounded-full px-4 h-11 text-xs font-semibold hover:bg-cream-200 inline-flex items-center gap-1.5"
                  title="Kaydedilmiş içeriği silip canlı sitedeki varsayılan HTML'e döner"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Varsayılana Dön
                </button>
              )}
              <button onClick={save} disabled={saving} data-testid="save-static" className="bg-ink-600 text-cream rounded-full px-6 h-11 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>

          <div className="mb-5">
            <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold">Sayfa Başlığı</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="mt-2 w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-11 text-base font-semibold outline-none" />
          </div>

          <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold block mb-2">İçerik (Zengin Metin Editörü)</label>
          <RichTextEditor
            value={content}
            onChange={setContent}
            placeholder="Bu sayfanın tam içeriğini buraya yazın. Başlık, paragraf, liste, alıntı, link, görsel ekleyebilirsiniz..."
            minHeight={420}
          />

          <div className="mt-5 p-4 rounded-xl bg-gold/10 border border-gold/30 text-xs text-ink-600">
            <strong>İpucu:</strong> İçerikte H2/H3 başlıklarla bölümler oluşturun, listeler ekleyin, önemli yerlerde alıntı kullanın. {usingDefault ? 'Şu anda canlı sitedeki varsayılan içerik editörde önizleniyor — istediğiniz gibi düzenleyip Kaydet\'e basınca site içeriği değişir.' : 'Boş bırakılırsa kodda yazılı varsayılan içerik gösterilir.'}
          </div>
        </div>
      </div>
    </div>
  );
}
